import { z } from 'zod';
import { route, defineContract } from '../core/types';
import { requiredStringSchema, projectIdParamsSchema, tzHeadersSchema } from '../schemas';
import {
	createStaticTagSchema,
	baseStaticTagApiInputSchema,
	updateStaticTagBodySchema,
	tradeSchema,
	baseManagerSchema,
} from './schemas';

// ============================================================
// Param Schemas
// ============================================================

/** `/projects/:projectId/staticTags/:tagId` — the project owns, `tagId` addresses. */
const staticTagParamsSchema = projectIdParamsSchema.extend({
	tagId: requiredStringSchema.describe('Tag identifier'),
});

/** `/projects/:projectId/staticTags/manager/:managerId` — reaches the tag via its manager. */
const staticTagManagerParamsSchema = projectIdParamsSchema.extend({
	managerId: requiredStringSchema.describe('Manager identifier'),
});

// ============================================================
// Body Schemas
// ============================================================

/**
 * `projectId` is deliberately absent from the manager body. `baseManagerSchema`
 * declares one, and it used to be the only project this route's caller supplied —
 * the handler loaded the tag by `managerId` and asserted membership on whatever
 * it found. The path carries the project now, so omitting the field from the
 * SCHEMA (rather than ignoring it in the handler) is what makes moving a
 * manager's tag between projects inexpressible.
 */
const updateTagManagerBodySchema = baseManagerSchema.omit({ projectId: true });

// ============================================================
// Contract
// ============================================================

/**
 * Static tags contract.
 *
 * `{ root: true }`, so every path here is spelled ABSOLUTELY — the contract-name
 * prefix is not applied. `updateTagsTrade` keeps its old `/static/...` path on
 * purpose (see below) and spells it in full for exactly the reason the playbook
 * gives: `root` strips the prefix from the whole file at once, not per route.
 *
 * The project field for the `staticTags` collection is `originProjectId` — these
 * documents have no `projectId` field at all. The `:projectId` in these paths is
 * matched against `originProjectId` everywhere in the service layer below.
 */
export const staticTagsContract = defineContract(
	'staticTags',
	{
		// ── Collection ────────────────────────────────────────
		// Two POSTs on one collection: `createTag` takes one tag, `insertStaticTags`
		// takes an array. An explicit `/bulk` sub-resource beats one route typed
		// `T | T[]` — the two differ in response and in duplicate-tagNick handling.
		createTag: route({
			method: 'POST',
			path: '/projects/:projectId/staticTags',
			params: projectIdParamsSchema,
			// `originProjectId` is NOT in this body. It never reached the central guard
			// anyway — `collectRequestProjectIds` reads `projectId`/`projectIds`, not
			// `originProjectId` — so the only thing between a caller and a foreign
			// project was a handler-level assert. The path is the claim now.
			body: createStaticTagSchema,
			response: z.string(),
			operationId: 'createTag',
			tags: ['Static Tags'],
			summary: 'Create a static tag',
		}),
		insertStaticTags: route({
			method: 'POST',
			path: '/projects/:projectId/staticTags/bulk',
			params: projectIdParamsSchema,
			body: z.array(baseStaticTagApiInputSchema),
			response: z.string(),
			operationId: 'insertStaticTags',
			tags: ['Static Tags'],
			summary: 'Insert static tags for a project',
		}),
		getStaticTagsByProject: route({
			method: 'GET',
			path: '/projects/:projectId/staticTags',
			params: projectIdParamsSchema,
			response: z.array(z.any()),
			operationId: 'getStaticTagsByProject',
			tags: ['Static Tags'],
			summary: 'Get static tags by project',
		}),
		// The `{ids}`-body variant owns the collection DELETE. Its delete-all twin
		// (`DELETE /static/tag?projectId=`) is GONE: no caller in this repo, none in
		// devops, none in trusstorutils, and the backoffice React-Admin dataProvider
		// never builds a bodyless collection DELETE for these resources.
		deleteManyTags: route({
			method: 'DELETE',
			path: '/projects/:projectId/staticTags',
			params: projectIdParamsSchema,
			body: z.object({ ids: z.array(z.string()) }),
			response: z.string(),
			operationId: 'deleteManyTags',
			tags: ['Static Tags'],
			summary: 'Delete multiple tags by IDs',
		}),

		// ── Sub-collections (static segments, matched before `/:tagId`) ─
		getManagerStaticTags: route({
			method: 'GET',
			path: '/projects/:projectId/staticTags/managers',
			params: projectIdParamsSchema,
			response: z.array(z.any()),
			operationId: 'getManagerStaticTags',
			tags: ['Static Tags'],
			summary: 'Get manager static tags by project',
		}),
		unassignAllTags: route({
			method: 'POST',
			path: '/projects/:projectId/staticTags/unassignAll',
			params: projectIdParamsSchema,
			response: z.string(),
			operationId: 'unassignAllTags',
			tags: ['Static Tags'],
			summary: 'Unassign all tags for a project',
		}),
		updateTagManager: route({
			method: 'PUT',
			path: '/projects/:projectId/staticTags/manager/:managerId',
			params: staticTagManagerParamsSchema,
			body: updateTagManagerBodySchema,
			headers: tzHeadersSchema,
			response: z.any(),
			operationId: 'updateTagManager',
			tags: ['Static Tags'],
			summary: 'Update tag manager assignment',
		}),

		// ── Items ─────────────────────────────────────────────
		getStaticTagByTagId: route({
			method: 'GET',
			path: '/projects/:projectId/staticTags/:tagId',
			params: staticTagParamsSchema,
			response: z.any(),
			operationId: 'getStaticTagByTagId',
			tags: ['Static Tags'],
			summary: 'Get a static tag by tag ID',
		}),
		updateStaticTag: route({
			method: 'PUT',
			path: '/projects/:projectId/staticTags/:tagId',
			params: staticTagParamsSchema,
			body: updateStaticTagBodySchema,
			// `project-id` is gone from these headers. It was BOTH the authorization
			// claim the audit flagged AND the project attributed to the BI events, the
			// log merge and the LocationEngine call — the path value is all three now,
			// and the two can no longer disagree.
			headers: tzHeadersSchema,
			response: z.any(),
			operationId: 'updateStaticTag',
			tags: ['Static Tags'],
			summary: 'Update a static tag',
		}),
		deleteStaticTagByTagId: route({
			method: 'DELETE',
			path: '/projects/:projectId/staticTags/:tagId',
			params: staticTagParamsSchema,
			response: z.string(),
			operationId: 'deleteStaticTagByTagId',
			tags: ['Static Tags'],
			summary: 'Delete a static tag by tag ID',
		}),
		unassignTag: route({
			method: 'PUT',
			path: '/projects/:projectId/staticTags/:tagId/unassign',
			params: staticTagParamsSchema,
			headers: tzHeadersSchema,
			response: z.any(),
			operationId: 'unassignTag',
			tags: ['Static Tags'],
			summary: 'Unassign a specific tag',
		}),

		// ── Global admin, NOT project scope ───────────────────
		/**
		 * A trade rename cascades across every static tag and every merged tag in
		 * every project of every organization — the same cross-organization cascade
		 * as `activities::updateTrade` and `safetyEngine::updateTrade`, both already
		 * declared global admin. Forcing `/projects/:projectId` onto it would be a
		 * claim the handler never reads. `allowTrusstorAdminRoles` is the gate;
		 * `PROJECT_LESS_BY_DESIGN` is where the decision is on the record.
		 */
		updateTagsTrade: route({
			method: 'PUT',
			path: '/static/updateTrade',
			body: tradeSchema,
			response: z.string(),
			operationId: 'updateTagsTrade',
			tags: ['Static Tags'],
			summary: 'Update tags trade assignment (global admin)',
		}),
	},
	{ root: true }
);
